import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import BuilderMoney from '../BuilderMoney';

/**
 * Attribute selection
 *
 * Attributes differ from addons in that attributes map to a product
 * "variation", effectively changing the product that is selected
 *
 * @author John Noel <john.noel@joipolloi.com>
 * @author Matt Haynes <Matthew.haynes@joipolloi.com>
 * @package Minifigs
 */
const Attributes = ({
    variations = {},
    activeSection,
    skintone = '',
    selected = 0,
    onChange = () => {},
    optionLabel,
}) => {
    let variationsVariantsArr = variations;

    if (typeof variationsVariantsArr === 'undefined') {
        return (
            <div className="attributes">
                <p>There are no attributes</p>
            </div>
        );
    } else {
        if (variationsVariantsArr.length === 1) {
            return (
                <div className="attributes">
                    <input name="id" value={variationsVariantsArr[0]?.id?.split('/').pop()} type="hidden" />
                </div>
            );
        }

        // Skin tone filtering heuristic:
        // 1. Explicit sections: heads, legs, arms
        // 2. Any product whose first option label suggests skin tone (e.g. "Skin Tone", "Skintone")
        const optionLabelLower = optionLabel?.toLowerCase?.() || '';
        const optionImpliesSkinTone = ['skin tone', 'skintone', 'skin-tone'].some((k) => optionLabelLower.includes(k));
        // Refined heuristic: Only filter by skin tone when:
        // 1. Section is heads or arms (always skin-dependent)
        // 2. Section is legs AND option label implies skin tone (to avoid filtering colour-only leg variants like skirts)
        // 3. Option label explicitly implies skin tone regardless of section
        // Must also have a skintone value.
        const isSkinToneFilteredSection =
            !!skintone &&
            (['heads', 'arms'].includes(activeSection) ||
                (activeSection === 'legs' && optionImpliesSkinTone) ||
                optionImpliesSkinTone);
        const lowerSkin = skintone?.toLowerCase?.();
        const removeNonMatching = isSkinToneFilteredSection;

        const normalisedVariants = variationsVariantsArr.map((attr) => (attr.id ? attr : attr.node));

        // Skin-tone filtering: tokens based solely on current canonical values (no legacy forms).
        // Canonical values: yellow, light, medium, nougat, dark
        const SKIN_TONE_TOKEN_MAP = {
            yellow: ['yellow'],
            light: ['light'],
            medium: ['medium'],
            nougat: ['nougat'],
            dark: ['dark'],
        };
        const skinToneTokens = (SKIN_TONE_TOKEN_MAP[lowerSkin] || [lowerSkin]).filter(Boolean);

        let enabledVariants = normalisedVariants.filter((v) => {
            if (!isSkinToneFilteredSection) return true;
            const titleLower = v?.title?.toLowerCase?.() || '';
            // Match explicit skin tone or any token
            return skinToneTokens.some((tok) => titleLower.includes(tok));
        });

        // Fallback: if filtering removed ALL variants (common for colour-only products under 'legs'), disable filtering.
        if (isSkinToneFilteredSection && enabledVariants.length === 0) {
            enabledVariants = normalisedVariants;
        }

        // If only ONE enabled variant (even if more total variants exist), hide the dropdown
        if (enabledVariants.length === 1) {
            const onlyVariant = enabledVariants[0];
            const onlyId = onlyVariant.id.split('/').pop();
            if (selected !== onlyId) {
                onChange(onlyVariant, onlyId);
            }
            return (
                <div className="attributes">
                    <input name="id" value={onlyId} type="hidden" />
                </div>
            );
        }

        // Auto-select first ENABLED variant if current selected is empty/invalid.
        const currentIsValid = enabledVariants.some((v) => v.id.split('/').pop() === selected);
        if (!currentIsValid && enabledVariants.length > 0) {
            const firstEnabled = enabledVariants[0];
            const firstEnabledId = firstEnabled.id.split('/').pop();
            onChange(firstEnabled, firstEnabledId);
        }

        return (
            <div className="attributes">
                <div className="product-variation">
                    <label htmlFor="product_variants">Choose a {optionLabel ? optionLabel : 'colour'}</label>
                    <div className="product-variation-input">
                        {/* Custom dropdown - does not use <option> tags to allow richer HTML */}
                        <CustomSelect
                            name="product_variants"
                            id="product_variants"
                            items={removeNonMatching ? enabledVariants : normalisedVariants}
                            value={selected}
                            onChange={(item, id) => {
                                onChange(item, id);
                            }}
                            renderItem={(item) => (
                                <>
                                    <span className="variant-title">{item.title}</span>
                                    <span className="variant-price">
                                        {' ('}
                                        <BuilderMoney amount={item.price.amount} sourceCurrencyCode={item.price.currencyCode} />
                                        {')'}
                                    </span>
                                </>
                            )}
                        />
                        {/* Hidden input for legacy form submissions that expect `id` */}
                        <input name="id" value={selected || ''} type="hidden" />
                    </div>
                    <span className="please-choose-warning">Please choose a product variant to continue.</span>
                </div>
            </div>
        );
    }
};

// CustomSelect: accessible, keyboard-navigable dropdown without <option> tags
const CustomSelect = ({ items = [], value = '', onChange = () => {}, renderItem, name, id }) => {
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const buttonRef = useRef(null);
    const listRef = useRef(null);

    // Determine selected item index
    const selectedIndex = items.findIndex((it) => it.id.split('/').pop() === String(value));

    useEffect(() => {
        if (open) {
            // when opening, focus the list for keyboard navigation
            requestAnimationFrame(() => {
                listRef.current?.focus();
                setHighlight(selectedIndex >= 0 ? selectedIndex : 0);
            });
        } else {
            setHighlight(-1);
        }
    }, [open, selectedIndex]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (!buttonRef.current) return;
            if (buttonRef.current.contains(e.target)) return;
            if (listRef.current && listRef.current.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const toggle = () => setOpen((v) => !v);

    const selectIndex = (idx) => {
        const item = items[idx];
        const idVal = item?.id?.split('/').pop();
        onChange(item, idVal);
        setOpen(false);
        // return focus to button
        buttonRef.current?.focus();
    };

    const onKeyDownList = (e) => {
        const max = items.length - 1;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => (h < max ? h + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => (h > 0 ? h - 1 : max));
        } else if (e.key === 'Home') {
            e.preventDefault();
            setHighlight(0);
        } else if (e.key === 'End') {
            e.preventDefault();
            setHighlight(max);
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (highlight >= 0) selectIndex(highlight);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
            buttonRef.current?.focus();
        }
    };

    return (
        <div className="custom-select" data-name={name}>
            <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={`${id}-listbox`}
                ref={buttonRef}
                className="custom-select-button"
                onClick={toggle}
            >
                {selectedIndex >= 0 ? (
                    <span className="custom-select-value">
                        {items[selectedIndex].title}{' '}
                        <span className="variant-price">
                            {' ('}
                            <BuilderMoney amount={items[selectedIndex].price.amount} sourceCurrencyCode={items[selectedIndex].price.currencyCode} />
                            {')'}
                        </span>
                    </span>
                ) : (
                    <span className="custom-select-placeholder">Choose an option</span>
                )}
                <span className="custom-select-caret" aria-hidden>
                    ▾
                </span>
            </button>
            {open && (
                <ul
                    id={`${id}-listbox`}
                    role="listbox"
                    tabIndex={0}
                    ref={listRef}
                    className="custom-select-list"
                    onKeyDown={onKeyDownList}
                    aria-activedescendant={highlight >= 0 ? `${id}-opt-${highlight}` : undefined}
                >
                    {items.map((item, idx) => {
                        const optId = `${id}-opt-${idx}`;
                        const isSelected = idx === selectedIndex;
                        const isHighlighted = idx === highlight;
                        return (
                            <li
                                key={item.id}
                                id={optId}
                                role="option"
                                aria-selected={isSelected}
                                className={`custom-select-item ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlight' : ''}`}
                                onMouseEnter={() => setHighlight(idx)}
                                onMouseLeave={() => setHighlight(-1)}
                                onClick={() => selectIndex(idx)}
                            >
                                <div className="custom-select-item-content">
                                    {renderItem ? renderItem(item) : item.title}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

CustomSelect.propTypes = {
    items: PropTypes.array,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    renderItem: PropTypes.func,
    name: PropTypes.string,
    id: PropTypes.string,
};

Attributes.propTypes = {
    activeSection: PropTypes.string,
    optionLabel: PropTypes.string,
    attributes: PropTypes.array.isRequired,
    terms: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    variations: PropTypes.array,
    selected: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    skintone: PropTypes.string,
};

export default Attributes;
